import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  CheckCircle, Circle, ChevronDown, ExternalLink,
  Building2, Hash, FileText, CreditCard, Award, Plane,
  Briefcase, MapPin, ArrowRight, Lock, Sparkles, MessageCircle,
  Send, X, Target, TrendingUp, Users,
} from 'lucide-react';

const ZM_GREEN = '#0d7a3e';
const ZM_ORANGE = '#f5821f';
const ZM_BLACK = '#1a1a2e';
const ZM_BLUE = '#1565c0';
const ZM_LIGHT = '#f0f4f0';
const ZM_GRAY = '#64748b';

/* ═══════════════════════════════════════════
   STEPS DATA — pulled from "Complete Guide"
   ═══════════════════════════════════════════ */
export const SETUP_STEPS = [
  {
    id: 'activity',
    icon: Target,
    title: 'Define Business Activity',
    authority: 'Self-Assessment',
    summary: 'Identify your industry, target market, ownership rules and funding strategy before any registration.',
    body: 'Before registering, decide what your business will do — many sectors in Zambia (mining, fintech, telecom, health) require regulatory approval before you can legally operate.',
    tasks: [
      'Choose industry / sector (agriculture, ICT, mining, tourism, fintech…)',
      'Define target market — local, regional, or export',
      'Check if licenses are required for your sector',
      'Confirm ownership rules (citizen-owned vs foreign)',
      'Outline a funding strategy',
    ],
    documents: ['Business idea brief', 'Sector restriction notes'],
    link: { label: 'Sector overview – Ministry of SME', url: 'https://www.msme.gov.zm/' },
  },
  {
    id: 'pacra',
    icon: Building2,
    title: 'Register Company with PACRA',
    authority: 'PACRA — Patents & Companies Registration Agency',
    summary: 'Reserve your name, submit incorporation documents and receive your Certificate of Incorporation.',
    body: 'PACRA handles all company and business name registration in Zambia. Submit founder IDs, registered address and director information.',
    tasks: [
      'Reserve company name',
      'Submit incorporation documents',
      'Pay registration fees',
      'Receive Certificate of Incorporation',
    ],
    documents: [
      'National ID (NRC) — for Zambian founders',
      'Passport copies — for foreign founders',
      'Proof of address',
      'Directors & shareholders information',
      'Parent company documents (if foreign company)',
      'Local registered address',
    ],
    link: { label: 'PACRA Online', url: 'https://www.pacra.org.zm/' },
  },
  {
    id: 'tpin',
    icon: Hash,
    title: 'Obtain TPIN (Tax ID)',
    authority: 'ZRA — Zambia Revenue Authority',
    summary: 'Get your Tax Payer Identification Number — required for taxes, banking and licenses.',
    body: 'TPIN is mandatory for every business in Zambia. You will need it for opening a bank account, applying for licenses, and filing taxes.',
    tasks: [
      'Apply for TPIN online via ZRA portal',
      'Submit PACRA certificate',
      'Provide directors\' IDs / passports',
      'Register business and contact details',
    ],
    documents: ['PACRA Certificate of Incorporation', 'Directors\' IDs/passports', 'Business contact details'],
    link: { label: 'ZRA TPIN Registration', url: 'https://www.zra.org.zm/' },
  },
  {
    id: 'taxes',
    icon: FileText,
    title: 'Register for Taxes',
    authority: 'ZRA — Zambia Revenue Authority',
    summary: 'Register the right tax types: Corporate Income Tax, PAYE, VAT, Withholding Tax, Customs Duties.',
    body: 'Every Zambian company pays Corporate Income Tax. Additional taxes apply depending on your activities (employees → PAYE, sales above threshold → VAT, imports → Customs Duties).',
    tasks: [
      'Register for Corporate Income Tax',
      'Register PAYE (if employing staff)',
      'Register for VAT (if above threshold)',
      'Activate Smart Invoice if applicable',
    ],
    documents: ['TPIN certificate', 'PACRA certificate', 'Business plan / projected turnover'],
    link: { label: 'ZRA Smart Invoice', url: 'https://www.zra.org.zm/smart-invoice/' },
  },
  {
    id: 'bank',
    icon: CreditCard,
    title: 'Open a Business Bank Account',
    authority: 'Commercial Bank',
    summary: 'A dedicated business account is required for accepting payments, payroll and tax compliance.',
    body: 'Most Zambian banks (Zanaco, Standard Chartered, Stanbic, FNB, Atlas Mara) offer SME accounts. Bring all incorporation, tax and director documents.',
    tasks: [
      'Choose a bank that fits your sector and size',
      'Prepare incorporation, TPIN and director documents',
      'Submit board resolution authorising the account',
      'Activate online banking and payment terminals',
    ],
    documents: ['Certificate of Incorporation', 'TPIN certificate', 'Board resolution', 'Directors\' IDs', 'Proof of business address'],
    link: { label: 'Bank of Zambia – licensed banks', url: 'https://www.boz.zm/' },
  },
  {
    id: 'licenses',
    icon: Award,
    title: 'Obtain Licenses & Permits',
    authority: 'Local Council + Sector Regulator',
    summary: 'Every business needs a Local Council Trading Licence. Some sectors require additional regulator approvals.',
    body: 'Common sector regulators: Mining (Ministry of Mines), Fintech (Bank of Zambia), Tourism (Ministry of Tourism), Telecom (ZICTA), Health (Ministry of Health), Environment (ZEMA).',
    tasks: [
      'Apply for Local Council Trading Licence',
      'Identify sector-specific regulators',
      'Submit sector licence applications',
      'Schedule inspections (if required)',
    ],
    documents: ['Trading licence application', 'Sector permits', 'Inspection certificates', 'Premises lease agreement'],
    link: { label: 'ZICTA (telecom/ICT)', url: 'https://www.zicta.zm/' },
  },
  {
    id: 'zda',
    icon: TrendingUp,
    title: 'Investment Certificate (ZDA)',
    authority: 'ZDA — Zambia Development Agency',
    summary: 'Optional but strongly recommended for tax incentives, investment protection and faster approvals.',
    body: 'Investments of $500,000 or more typically qualify for major incentives. Smaller SMEs can still apply for facilitation support.',
    tasks: [
      'Prepare a business plan and financial projections',
      'Submit investment certificate application',
      'Confirm employment projections',
      'Receive investment certificate',
    ],
    documents: ['Business plan', 'Proof of investment funds', 'Employment projections', 'Sector justification'],
    link: { label: 'Zambia Development Agency', url: 'https://www.zda.org.zm/' },
  },
  {
    id: 'immigration',
    icon: Plane,
    title: 'Immigration (Foreigners Only)',
    authority: 'Department of Immigration',
    summary: 'Foreign investors and key staff need Investor or Work Permits before operating.',
    body: 'Investor permits are tied to proof of investment. Work permits require local registration and police clearance.',
    tasks: [
      'Apply for Investor Permit',
      'Apply for Work Permit (key staff)',
      'Obtain police clearance',
      'Submit business registration documents',
    ],
    documents: ['Passport', 'Business registration', 'Proof of investment', 'Police clearance', 'Business plan'],
    link: { label: 'Zambia Immigration', url: 'https://www.zambiaimmigration.gov.zm/' },
  },
  {
    id: 'employment',
    icon: Users,
    title: 'Employment Compliance',
    authority: 'Ministry of Labour + NAPSA',
    summary: 'If you hire staff, register as an employer and meet pension, labour and tax obligations.',
    body: 'Employees must be registered with NAPSA for pension, with Workers\' Compensation Fund and through PAYE for tax.',
    tasks: [
      'Register with Ministry of Labour',
      'Register with NAPSA (pension)',
      'Register Workers\' Compensation',
      'Register PAYE with ZRA',
      'Issue written employment contracts',
    ],
    documents: ['Employer registration form', 'Employee NRC/passport copies', 'Contracts and job descriptions'],
    link: { label: 'NAPSA – pension registration', url: 'https://www.napsa.co.zm/' },
  },
];

const totalTaskCount = SETUP_STEPS.reduce((acc, s) => acc + s.tasks.length, 0);

/* ═══════════════════════════════════════════
   BUSINESS SETUP GUIDE — Public + Dashboard
   ═══════════════════════════════════════════ */
const STORAGE_KEY = 'crossbase_setup_progress_v1';

const useProgress = () => {
  const [done, setDone] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(done)); } catch {}
  }, [done]);
  const toggle = (stepId, taskIdx) => {
    const k = `${stepId}::${taskIdx}`;
    setDone(prev => ({ ...prev, [k]: !prev[k] }));
  };
  const isDone = (stepId, taskIdx) => !!done[`${stepId}::${taskIdx}`];
  const stepCompletedCount = (stepId) => {
    const step = SETUP_STEPS.find(s => s.id === stepId);
    if (!step) return 0;
    return step.tasks.reduce((acc, _, i) => acc + (done[`${stepId}::${i}`] ? 1 : 0), 0);
  };
  const overallDoneCount = Object.values(done).filter(Boolean).length;
  const overallPercent = Math.round((overallDoneCount / totalTaskCount) * 100);
  const reset = () => setDone({});
  return { isDone, toggle, stepCompletedCount, overallPercent, overallDoneCount, reset };
};

export const BusinessSetupGuide = ({ compact = false, onClose }) => {
  const { isDone, toggle, stepCompletedCount, overallPercent, overallDoneCount, reset } = useProgress();
  const [openStep, setOpenStep] = useState(SETUP_STEPS[0].id);

  const stepStatus = (stepId) => {
    const step = SETUP_STEPS.find(s => s.id === stepId);
    const c = stepCompletedCount(stepId);
    if (c === 0) return 'pending';
    if (c === step.tasks.length) return 'done';
    return 'in_progress';
  };

  return (
    <div data-testid="business-setup-guide" className={compact ? '' : 'max-w-4xl mx-auto px-4 py-8'}>
      {/* Header / progress */}
      <div className="rounded-2xl p-6 mb-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${ZM_GREEN} 0%, #0a5c2e 100%)` }}>
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-10" style={{ background: ZM_ORANGE }} />
        <div className="absolute -right-20 bottom-0 w-32 h-32 rounded-full opacity-10" style={{ background: 'white' }} />
        <div className="relative z-10 flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold mb-2" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <Sparkles size={11} /> Interactive Guide
            </div>
            <h2 className="text-2xl font-bold mb-1">How to Start a Business in Zambia</h2>
            <p className="text-sm opacity-80 mb-4">Follow the journey step-by-step. Tick off each task as you go and the platform tracks your progress.</p>
            <div className="flex items-center gap-3 mb-1">
              <span className="text-xs font-medium opacity-80">{overallDoneCount} of {totalTaskCount} tasks complete</span>
              <span className="text-2xl font-bold tabular-nums">{overallPercent}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.25)' }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: ZM_ORANGE }}
                initial={{ width: 0 }}
                animate={{ width: `${overallPercent}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            {onClose && (
              <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }}>Close</button>
            )}
            <button onClick={reset} className="text-[10px] px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }}>Reset</button>
          </div>
        </div>
      </div>

      {/* Steps timeline */}
      <div className="relative">
        {/* Vertical connector */}
        <div className="absolute left-[27px] top-4 bottom-4 w-[2px]" style={{ background: '#e2e8f0' }} />

        {SETUP_STEPS.map((step, idx) => {
          const Icon = step.icon;
          const status = stepStatus(step.id);
          const isOpen = openStep === step.id;
          const c = stepCompletedCount(step.id);
          const total = step.tasks.length;
          const pct = Math.round((c / total) * 100);
          const isLocked = idx > 0 && stepStatus(SETUP_STEPS[idx - 1].id) === 'pending';

          const color = status === 'done' ? ZM_GREEN : status === 'in_progress' ? ZM_ORANGE : ZM_GRAY;

          return (
            <div key={step.id} className="relative pl-16 pb-4" data-testid={`setup-step-${step.id}`}>
              {/* Step number badge */}
              <motion.div
                initial={false}
                animate={{ scale: isOpen ? 1.05 : 1 }}
                className="absolute left-0 top-2 w-14 h-14 rounded-full flex items-center justify-center font-bold text-white shadow-lg z-10"
                style={{ background: color, boxShadow: `0 4px 14px ${color}55` }}
              >
                {status === 'done' ? <CheckCircle size={22} /> : <Icon size={20} />}
              </motion.div>

              {/* Step card */}
              <motion.div
                whileHover={{ y: -1 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
                style={{ border: status === 'done' ? `2px solid ${ZM_GREEN}` : `1px solid #e2e8f0` }}
              >
                <button
                  onClick={() => !isLocked && setOpenStep(isOpen ? null : step.id)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isLocked}
                  data-testid={`setup-step-toggle-${step.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>Step {idx + 1}</span>
                      {status === 'done' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: ZM_GREEN }}>COMPLETE</span>}
                      {status === 'in_progress' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: ZM_ORANGE }}>IN PROGRESS</span>}
                      {isLocked && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" style={{ background: '#f1f5f9', color: ZM_GRAY }}><Lock size={9} /> Locked</span>}
                    </div>
                    <h3 className="text-sm sm:text-base font-bold" style={{ color: ZM_BLACK }}>{step.title}</h3>
                    <p className="text-[11px] mt-0.5 truncate" style={{ color: ZM_GRAY }}>{step.authority}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-xs font-bold tabular-nums" style={{ color }}>{c}/{total}</span>
                    <div className="w-20 h-1.5 rounded-full overflow-hidden" style={{ background: '#f1f5f9' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                  <ChevronDown size={16} style={{ color: ZM_GRAY, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-2 border-t" style={{ borderColor: '#f1f5f9' }}>
                        <p className="text-xs leading-relaxed mb-4" style={{ color: ZM_GRAY }}>{step.body}</p>

                        {/* Tasks checklist */}
                        <div className="mb-4">
                          <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: ZM_BLACK }}>Tasks</div>
                          <div className="space-y-1.5">
                            {step.tasks.map((task, ti) => {
                              const checked = isDone(step.id, ti);
                              return (
                                <button
                                  key={ti}
                                  onClick={() => toggle(step.id, ti)}
                                  data-testid={`setup-task-${step.id}-${ti}`}
                                  className="w-full flex items-start gap-2.5 p-2.5 rounded-lg text-left transition-all hover:bg-gray-50"
                                  style={{ background: checked ? `${ZM_GREEN}10` : 'transparent', border: checked ? `1px solid ${ZM_GREEN}40` : '1px solid #e2e8f0' }}
                                >
                                  {checked
                                    ? <CheckCircle size={16} style={{ color: ZM_GREEN }} className="flex-shrink-0 mt-0.5" />
                                    : <Circle size={16} style={{ color: ZM_GRAY }} className="flex-shrink-0 mt-0.5" />}
                                  <span className={`text-xs ${checked ? 'line-through' : ''}`} style={{ color: checked ? ZM_GRAY : ZM_BLACK }}>{task}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Documents */}
                        {step.documents.length > 0 && (
                          <div className="mb-4">
                            <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: ZM_BLACK }}>Required Documents</div>
                            <div className="flex flex-wrap gap-1.5">
                              {step.documents.map((d, di) => (
                                <span key={di} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full" style={{ background: ZM_LIGHT, color: ZM_GREEN }}>
                                  <FileText size={9} /> {d}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Authority link + next */}
                        <div className="flex flex-wrap items-center justify-between gap-2 pt-3" style={{ borderTop: `1px dashed #e2e8f0` }}>
                          {step.link && (
                            <a href={step.link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:brightness-95" style={{ background: ZM_LIGHT, color: ZM_GREEN }}>
                              <ExternalLink size={11} /> {step.link.label}
                            </a>
                          )}
                          {idx < SETUP_STEPS.length - 1 && (
                            <button
                              onClick={() => setOpenStep(SETUP_STEPS[idx + 1].id)}
                              className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg text-white transition-all hover:brightness-110"
                              style={{ background: status === 'done' ? ZM_GREEN : ZM_ORANGE }}
                            >
                              Next: {SETUP_STEPS[idx + 1].title} <ArrowRight size={12} />
                            </button>
                          )}
                          {idx === SETUP_STEPS.length - 1 && status === 'done' && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg text-white" style={{ background: ZM_GREEN }}>
                              <CheckCircle size={12} /> All steps done — congratulations!
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          );
        })}
      </div>

      {/* Funding & support footer */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-4 rounded-xl" style={{ background: ZM_LIGHT }}>
          <div className="flex items-center gap-2 mb-2"><Briefcase size={16} style={{ color: ZM_GREEN }} /><span className="text-sm font-bold" style={{ color: ZM_BLACK }}>Funding & Support</span></div>
          <ul className="text-xs space-y-1" style={{ color: ZM_GRAY }}>
            <li>• <strong>CEEC</strong> — Citizens Economic Empowerment Commission (loans & grants)</li>
            <li>• <strong>CDF</strong> — Constituency Development Fund (local SMEs)</li>
            <li>• <strong>Ministry of SMEs</strong> — training & market access</li>
            <li>• <strong>ZDA</strong> — investment incentives & facilitation</li>
            <li>• <strong>Development Bank of Zambia</strong> — long-term SME financing</li>
          </ul>
        </div>
        <div className="p-4 rounded-xl" style={{ background: '#fff7ed', border: `1px solid ${ZM_ORANGE}30` }}>
          <div className="flex items-center gap-2 mb-2"><MapPin size={16} style={{ color: ZM_ORANGE }} /><span className="text-sm font-bold" style={{ color: ZM_BLACK }}>Citizen Empowerment Categories</span></div>
          <ul className="text-xs space-y-1" style={{ color: ZM_GRAY }}>
            <li>• <strong>Citizen-owned</strong> — ≥ 50.1% Zambian ownership</li>
            <li>• <strong>Citizen-empowered</strong> — 25–50% Zambian ownership</li>
            <li>• <strong>Citizen-influenced</strong> — 5–25% Zambian ownership</li>
            <li>• Some sectors (small-scale trade, transport) may be reserved for citizens</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SME VOXA HELPDESK — Static Chatbot
   ═══════════════════════════════════════════ */

const KB = [
  {
    keywords: ['pacra', 'register company', 'incorporation', 'company registration', 'name reservation'],
    answer: '**PACRA Registration (Step 2)**\n\nReserve your company name with PACRA, submit incorporation documents, pay the fees and you will receive a Certificate of Incorporation.\n\nDocuments you will need:\n• National ID (NRC) for Zambian founders, or passport copies for foreigners\n• Proof of address\n• Director and shareholder details\n• Local registered address\n\nApply online at pacra.org.zm.',
    chips: ['How long does PACRA take?', 'What is TPIN?'],
  },
  {
    keywords: ['tpin', 'tax id', 'zra', 'tax payer'],
    answer: '**TPIN — Tax Payer Identification Number (Step 3)**\n\nIssued by the Zambia Revenue Authority (ZRA). You need TPIN for taxes, banking and most licences.\n\nRequirements:\n• PACRA Certificate of Incorporation\n• Directors\' IDs / passports\n• Business and contact details\n\nApply at zra.org.zm.',
    chips: ['Which taxes apply?', 'How do I open a bank account?'],
  },
  {
    keywords: ['vat', 'paye', 'corporate tax', 'tax', 'taxes', 'smart invoice'],
    answer: '**Tax Registration (Step 4)**\n\n• **Corporate Income Tax** — required for every registered company\n• **PAYE** — required if you employ staff\n• **VAT** — required if turnover is above the threshold\n• **Withholding Tax** — applies to certain payments\n• **Customs Duties** — for imports/exports\n\nMost businesses also activate ZRA Smart Invoice for digital invoicing.',
    chips: ['What is the VAT threshold?', 'How do I register PAYE?'],
  },
  {
    keywords: ['bank account', 'business bank', 'open bank', 'banking'],
    answer: '**Business Bank Account (Step 5)**\n\nBring with you:\n• Certificate of Incorporation (PACRA)\n• TPIN certificate\n• Board resolution authorising the account\n• Directors\' IDs\n• Proof of business address\n\nMain SME banks: Zanaco, Stanbic, Standard Chartered, FNB, Atlas Mara.',
    chips: ['Do I need a TPIN first?', 'What licences do I need?'],
  },
  {
    keywords: ['license', 'licence', 'permit', 'trading licence', 'sector', 'council'],
    answer: '**Licenses & Permits (Step 6)**\n\nEvery business needs a **Local Council Trading Licence**. Sector-specific licences may also be required:\n\n• Mining → Ministry of Mines\n• Banking / Fintech → Bank of Zambia\n• Tourism → Ministry of Tourism\n• Telecom / ICT → ZICTA\n• Health → Ministry of Health\n• Construction → NCC\n• Energy → ERB\n• Environment → ZEMA',
    chips: ['Tell me about ZDA', 'Foreign investor steps?'],
  },
  {
    keywords: ['zda', 'investment certificate', 'incentives', 'investment'],
    answer: '**ZDA Investment Certificate (Step 7)**\n\nThe Zambia Development Agency (ZDA) issues investment certificates that grant tax incentives, investment protection and faster approvals.\n\nMajor incentives typically require investment ≥ USD 500,000, plus:\n• Business plan\n• Financial proof\n• Employment projections',
    chips: ['Can foreigners invest?', 'How does CEEC help SMEs?'],
  },
  {
    keywords: ['foreigner', 'foreign', 'investor permit', 'work permit', 'immigration', 'visa'],
    answer: '**Foreign Investors (Step 8)**\n\nForeign business owners must apply for:\n• **Investor Permit** — tied to proof of investment\n• **Work Permit** — for foreign staff\n\nDocuments:\n• Passport\n• Business registration\n• Proof of investment\n• Police clearance\n• Business plan\n\nApply via the Department of Immigration.',
    chips: ['What is citizen empowerment?', 'How much investment is needed?'],
  },
  {
    keywords: ['employee', 'employees', 'employment', 'staff', 'hire', 'napsa', 'pension', 'paye'],
    answer: '**Employment Compliance (Step 9)**\n\nIf you hire staff you must:\n• Register with the **Ministry of Labour**\n• Register with **NAPSA** for pension\n• Register **Workers\' Compensation**\n• Register **PAYE** with ZRA\n• Issue written employment contracts',
    chips: ['How does PAYE work?', 'What does NAPSA cost?'],
  },
  {
    keywords: ['ceec', 'funding', 'grant', 'loan', 'finance', 'capital', 'cdf'],
    answer: '**Funding & Support Options**\n\n• **CEEC** (Citizens Economic Empowerment Commission) — loans, grants, training for SMEs, youth, women\n• **CDF** (Constituency Development Fund) — local funding via councils\n• **Ministry of SMEs** — training, business development, market access\n• **ZDA** — investment facilitation & incentives\n• **Development Bank of Zambia** — long-term financing\n• Commercial banks, microfinance, impact funds',
    chips: ['How do I apply to CEEC?', 'What is the SME definition?'],
  },
  {
    keywords: ['citizen', 'empowerment', 'ownership'],
    answer: '**Citizen Empowerment Categories**\n\n• **Citizen-owned** — at least 50.1% Zambian ownership\n• **Citizen-empowered** — 25–50% Zambian ownership\n• **Citizen-influenced** — 5–25% Zambian ownership\n\nSome sectors (small-scale trade, transport) may be reserved for Zambian citizens.',
    chips: ['Tell me about ZDA', 'How do I register a company?'],
  },
  {
    keywords: ['structure', 'sole', 'partnership', 'private limited', 'cooperative', 'company type', 'business type'],
    answer: '**Common Business Structures**\n\nFor Zambian citizens:\n• **Sole Proprietorship / Business Name** — small or informal businesses\n• **Partnership** — 2+ local founders\n• **Private Limited Company** — most SMEs and scalable businesses\n• **Cooperative** — agriculture or community ventures\n• **Company Limited by Guarantee / NGO** — non-profits\n\nFor foreign investors: Local Subsidiary, Branch, Joint Venture, or ZDA Investment Route.',
    chips: ['Foreign investor steps?', 'Sector restrictions?'],
  },
  {
    keywords: ['mistake', 'common mistakes', 'avoid'],
    answer: '**Common Mistakes to Avoid**\n\n• Operating without licences\n• Ignoring tax obligations\n• Skipping employment compliance (NAPSA, PAYE)\n• Weak business plan\n• Not understanding sector rules\n• Missing annual filings\n• Poor financial records',
    chips: ['What documents do I need?', 'Tell me about taxes'],
  },
  {
    keywords: ['document', 'documents', 'checklist', 'paperwork'],
    answer: '**Core Documents Checklist**\n\n• Certificate of Incorporation (PACRA)\n• TPIN certificate (ZRA)\n• Tax registrations\n• Business plan\n• Bank account proof\n• Trading & sector licences\n• Company constitution / Articles\n\nForeigners also need: passports, parent company documents, investment proof, work/investor permit.',
    chips: ['How long does the process take?', 'What does it cost?'],
  },
];

const DEFAULT_CHIPS = [
  'How do I register a company?',
  'What is TPIN?',
  'Which licences do I need?',
  'How does CEEC funding work?',
  'Foreign investor steps?',
];

const matchAnswer = (text) => {
  const q = (text || '').toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const entry of KB) {
    let score = 0;
    for (const kw of entry.keywords) {
      if (q.includes(kw)) score += kw.length;
    }
    if (score > bestScore) { bestScore = score; best = entry; }
  }
  if (best) return best;
  return {
    answer: 'I can help with starting and registering a business in Zambia. Try asking about **PACRA**, **TPIN**, **taxes**, **licences**, **ZDA**, **CEEC funding**, **employment compliance** or **foreign investor permits**.',
    chips: DEFAULT_CHIPS,
  };
};

const renderMarkdown = (text) => {
  // Simple bold + line breaks renderer (no external dependency)
  const parts = text.split('\n').map((line, i) => {
    const segments = [];
    let last = 0;
    const regex = /\*\*(.+?)\*\*/g;
    let m;
    while ((m = regex.exec(line))) {
      if (m.index > last) segments.push(line.slice(last, m.index));
      segments.push(<strong key={`b-${i}-${m.index}`} style={{ color: ZM_BLACK }}>{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < line.length) segments.push(line.slice(last));
    return <div key={i} className={line.trim() === '' ? 'h-2' : ''}>{segments}</div>;
  });
  return parts;
};

export const SMEVoxaHelpDesk = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: '👋 Hi! I\'m **VOXA**, your SME HelpDesk assistant. Ask me anything about starting and running a business in Zambia.',
      chips: DEFAULT_CHIPS,
    },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, typing]);

  const sendQuery = (text) => {
    const value = (text || '').trim();
    if (!value) return;
    setMessages(prev => [...prev, { role: 'user', text: value }]);
    setInput('');
    setTyping(true);
    setTimeout(() => {
      const found = matchAnswer(value);
      setMessages(prev => [...prev, { role: 'bot', text: found.answer, chips: found.chips || DEFAULT_CHIPS }]);
      setTyping(false);
    }, 550);
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="voxa-helpdesk-open-btn"
            onClick={() => setOpen(true)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-full text-white shadow-2xl"
            style={{ background: ZM_GREEN, boxShadow: `0 8px 24px ${ZM_GREEN}66` }}
          >
            <MessageCircle size={18} />
            <span className="text-sm font-semibold">VOXA HelpDesk</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="voxa-helpdesk-panel"
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-5 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-2rem)] flex flex-col rounded-2xl bg-white overflow-hidden"
            style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
          >
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between text-white" style={{ background: `linear-gradient(135deg, ${ZM_GREEN} 0%, #0a5c2e 100%)` }}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: ZM_ORANGE }}>V</div>
                <div>
                  <div className="text-sm font-bold">SME VOXA HelpDesk</div>
                  <div className="text-[10px] opacity-80 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#22c55e' }} /> Online — demo assistant
                  </div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/15" data-testid="voxa-helpdesk-close-btn"><X size={16} /></button>
            </div>

            {/* Messages */}
            <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: '#f8fafc' }}>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-3 py-2.5 rounded-2xl text-xs leading-relaxed ${m.role === 'user' ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                       style={{
                         background: m.role === 'user' ? ZM_GREEN : 'white',
                         color: m.role === 'user' ? 'white' : ZM_BLACK,
                         boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                       }}>
                    {renderMarkdown(m.text)}
                    {m.role === 'bot' && m.chips && (
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {m.chips.map((c, ci) => (
                          <button key={ci} onClick={() => sendQuery(c)} className="text-[10px] font-medium px-2 py-1 rounded-full transition-all hover:brightness-95" style={{ background: ZM_LIGHT, color: ZM_GREEN }} data-testid={`voxa-chip-${i}-${ci}`}>
                            {c}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex justify-start">
                  <div className="px-3 py-2.5 rounded-2xl rounded-bl-sm bg-white text-xs flex items-center gap-1" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                    <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0 }} className="w-1.5 h-1.5 rounded-full" style={{ background: ZM_GRAY }} />
                    <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0.2 }} className="w-1.5 h-1.5 rounded-full" style={{ background: ZM_GRAY }} />
                    <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0.4 }} className="w-1.5 h-1.5 rounded-full" style={{ background: ZM_GRAY }} />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t" style={{ borderColor: '#e2e8f0' }}>
              <form onSubmit={(e) => { e.preventDefault(); sendQuery(input); }} className="flex items-center gap-2">
                <input
                  data-testid="voxa-helpdesk-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about PACRA, TPIN, licences…"
                  className="flex-1 px-3 py-2 rounded-full text-xs outline-none"
                  style={{ background: '#f1f5f9', color: ZM_BLACK }}
                />
                <button type="submit" data-testid="voxa-helpdesk-send-btn" className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all hover:brightness-110" style={{ background: ZM_GREEN }}>
                  <Send size={14} />
                </button>
              </form>
              <p className="text-[9px] mt-1.5 text-center" style={{ color: ZM_GRAY }}>Demo assistant — pre-written answers based on the Crossbase business guide.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/* ═══════════════════════════════════════════
   COMPACT DASHBOARD WIDGET
   ═══════════════════════════════════════════ */
export const SetupGuideWidget = ({ onOpenFull }) => {
  const { stepCompletedCount, overallPercent } = useProgress();

  const nextStep = useMemo(() => {
    for (const s of SETUP_STEPS) {
      if (stepCompletedCount(s.id) < s.tasks.length) return s;
    }
    return null;
  }, [stepCompletedCount]);

  return (
    <div className="rounded-xl bg-white shadow-sm overflow-hidden" data-testid="setup-guide-widget">
      <div className="p-4 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${ZM_GREEN} 0%, #0a5c2e 100%)` }}>
        <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-15" style={{ background: ZM_ORANGE }} />
        <div className="relative z-10 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-1">Business Setup Journey</div>
            <h3 className="text-base font-bold mb-1">How to start your business in Zambia</h3>
            <p className="text-[11px] opacity-80 truncate">{nextStep ? `Next up: ${nextStep.title}` : 'All steps complete — congratulations!'}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-2xl font-bold tabular-nums">{overallPercent}%</div>
            <div className="text-[10px] opacity-80">complete</div>
          </div>
        </div>
        <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.25)' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${overallPercent}%`, background: ZM_ORANGE }} />
        </div>
      </div>
      <div className="p-3 flex items-center gap-2">
        <div className="flex-1 grid grid-cols-9 gap-1">
          {SETUP_STEPS.map((s) => {
            const c = stepCompletedCount(s.id);
            const t = s.tasks.length;
            const pct = c / t;
            const color = pct === 1 ? ZM_GREEN : pct > 0 ? ZM_ORANGE : '#e2e8f0';
            return <div key={s.id} className="h-6 rounded" style={{ background: color }} title={s.title} />;
          })}
        </div>
        <button onClick={onOpenFull} className="flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg text-white transition-all hover:brightness-110 flex-shrink-0" style={{ background: ZM_GREEN }} data-testid="open-full-guide-btn">
          Continue <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};
